import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, switchMap, throwError, Subject, EMPTY } from 'rxjs';
import { StorageService } from '../../account/services/storage.service';
import { AuthService } from '../../account/services/auth.service';
// export const httpRequestInterceptor: HttpInterceptorFn = (req, next) => {
//   return next(req);
// };

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: Subject<any> = new Subject<any>();

  constructor(
    private storageService: StorageService,
    private authService: AuthService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Token is sourced via AuthService: in Authorization Code mode it comes from keycloak-js
    // (refreshed on demand); in password mode it comes from StorageService.
    return this.authService.getValidAccessToken().pipe(
      switchMap((token) => this.handleWithToken(req, next, token)),
    );
  }

  private handleWithToken(req: HttpRequest<any>, next: HttpHandler, token: string | null): Observable<HttpEvent<any>> {
    let authReq = req;
    if (token) {
      authReq = req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + token),
        withCredentials: true,
      });
    }

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && !req.url.includes('token')) {
          // Handle 401 Unauthorized - token expired, try refresh
          if (error.status === 401) {
            return this.handle401Error(authReq, next);
          }
          // Handle 403 Forbidden - may indicate session invalidation
          if (error.status === 403 && this.isAuthRelatedError(error)) {
            return this.handle401Error(authReq, next);
          }
        }
        if (error.status == 413) {
          return throwError(() => new Error('File upload failed. The file size exceeds the allowed limit.'));
        }
        let errorMessage = error?.error?.error_description || error?.message;
        console.error('HTTP Request Error:', errorMessage, 'Source:', req.url);
        return throwError(() => new Error(`Error: ${errorMessage}`));
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject = new Subject<any>();

      const refreshToken = this.storageService.getRefreshToken();
      const canRefresh = this.authService.isAuthCodeFlow || !!refreshToken;

      if (canRefresh) {
        return this.authService.refreshToken().pipe(
          switchMap((newToken: any) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(newToken.access_token);
            this.refreshTokenSubject.complete();

            return next.handle(request.clone({
              headers: request.headers.set('Authorization', 'Bearer ' + newToken.access_token),
              withCredentials: true,
            }));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.error(err);
            // forceLogoutAndRedirect is already called inside refreshToken()
            return EMPTY;
          })
        );
      } else {
        this.isRefreshing = false;
        this.refreshTokenSubject.error('No refresh token');
        // forceLogoutAndRedirect is already called inside refreshToken()
        this.authService.refreshToken().subscribe(); // triggers forceLogoutAndRedirect
        return EMPTY;
      }
    } else {
      // Refresh already in progress — queue this request
      return this.refreshTokenSubject.pipe(
        switchMap((token) => {
          if (token) {
            const newAuthReq = request.clone({
              headers: request.headers.set('Authorization', 'Bearer ' + token),
              withCredentials: true,
            });
            return next.handle(newAuthReq);
          }
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError(() => {
          return throwError(() => new Error('Token refresh already in progress failed'));
        })
      );
    }
  }

  /**
   * Check if error indicates an authentication/session issue
   */
  private isAuthRelatedError(error: HttpErrorResponse): boolean {
    const errorDesc = error?.error?.error_description || error?.error?.error || '';
    return errorDesc.includes('token') ||
           errorDesc.includes('session') ||
           errorDesc.includes('expired') ||
           errorDesc.includes('invalid');
  }
}

export const httpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
];
